import { isDefined } from '@kaoto/forms';

import { getParsedValue } from './get-parsed-value';
import { getValue } from './get-value';

export type ParsedParameters = Record<string, string | boolean | number>;

interface SyntaxAnalysis {
  schema: string;
  syntax: string;
  delimiters: string[] | null;
  delimiterRegex: RegExp | null;
  keys: string[];
}

/**
 * Helper class for working with Camel URIs
 */
export class CamelUriHelper {
  private static readonly URI_SEPARATORS_REGEX = /:|(\/\/)|#|\//g;
  private static readonly KNOWN_URI_MAP: Record<string, string> = {
    'http://httpUri': 'http://',
    'https://httpUri': 'https://',
  };
  private static readonly COMPONENT_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*$/;
  private static readonly KAMELET_NAME_REGEX = /^[a-z0-9][a-z0-9-]*$/;
  // eslint-disable-next-line no-control-regex
  private static readonly DANGEROUS_URI_SEGMENT_REGEX = /(\.\.|<|>|\$\{|{{|[\u0000-\u001F\u007F])/;

  static getUriString<T>(value: T | undefined | null): string | undefined {
    /** For string-based processor definitions, we can return the definition itself */
    if (typeof value === 'string' && value !== '') {
      return value;
    }

    const uriString = getValue(value, 'uri');

    /** For object-based processor definitions, we can return the `uri` property if not empty */
    if (typeof uriString === 'string' && uriString !== '') {
      return uriString;
    }

    return undefined;
  }

  private static cleanUriParts(uri: string, syntax: string): string {
    return uri.replace(this.KNOWN_URI_MAP[syntax], '');
  }

  /** Return a new object ignoring the parameters from the `keys` array*/
  private static filterParameters(parameters: ParsedParameters, keys: string[]): ParsedParameters {
    return Object.keys(parameters).reduce((acc, parameterKey) => {
      if (!keys.includes(parameterKey)) {
        acc[parameterKey] = parameters[parameterKey];
      }
      return acc;
    }, {} as ParsedParameters);
  }

  private static readonly REGEX_EXPRESSION = /^\s*(\{\{.*\}\}|\$\{.*\})\s*$/;

  private static isExpression(value: string): boolean {
    return this.REGEX_EXPRESSION.test(value);
  }

  private static createQueryString(parameters: ParsedParameters): string {
    if (!parameters || Object.keys(parameters).length === 0) {
      return '';
    }

    return Object.keys(parameters)
      .filter((key) => parameters[key] !== undefined)
      .map((key) => {
        const rawValue = parameters[key].toString();
        const value = this.isExpression(rawValue) ? rawValue : encodeURIComponent(rawValue);
        return `${key}=${value}`;
      })
      .join('&');
  }

  static getUriStringFromParameters(
    originalUri: string,
    uriSyntax: string,
    parameters?: ParsedParameters,
    options?: { requiredParameters?: string[]; defaultValues?: ParsedParameters },
  ): string {
    const { schema, syntax: syntaxWithoutScheme, delimiters, keys } = this.analyzeSyntax(uriSyntax);
    /** Prepare options */
    const requiredParameters = options?.requiredParameters ?? [];
    const defaultValues = options?.defaultValues ?? {};

    /** If the syntax does not contain any delimiters, we can return the URI string as is */
    if (
      syntaxWithoutScheme === '' ||
      (delimiters === null && parameters?.[syntaxWithoutScheme] === undefined) ||
      !isDefined(parameters)
    ) {
      const paramQueryString = this.createQueryString(parameters ?? {});
      return paramQueryString && paramQueryString !== '' ? originalUri + '?' + paramQueryString : originalUri;
    } else if (delimiters === null) {
      const value = parameters?.[syntaxWithoutScheme] ?? defaultValues[syntaxWithoutScheme] ?? '';
      const uri = `${schema}:${this.cleanUriParts(value.toString(), uriSyntax)}`;
      const filteredParameters = this.filterParameters(parameters, [syntaxWithoutScheme]);

      const paramQueryString = this.createQueryString(filteredParameters ?? {});
      return paramQueryString && paramQueryString !== '' ? uri + '?' + paramQueryString : uri;
    }

    const values = keys.map((key, keyIndex) => {
      const valueOrUndefined = parameters[key] === '' ? undefined : parameters[key];
      const value = valueOrUndefined ?? defaultValues[key] ?? '';
      const isRequired = requiredParameters.includes(key);
      const previousDelimiter = keyIndex > 0 ? delimiters[keyIndex - 1] : ':';

      return { key, value, isRequired, previousDelimiter };
    });
    values.unshift({ key: 'schema', value: schema, isRequired: true, previousDelimiter: '' });

    const uri = values.reduceRight((acc, current, index) => {
      const isNextSegmentRequired = values[index + 1]?.isRequired;
      if (!current.isRequired && current.value === '' && !isNextSegmentRequired) {
        return acc;
      }

      const cleanValue = this.cleanUriParts(current.value.toString(), uriSyntax);
      return `${current.previousDelimiter}${cleanValue}${acc}`;
    }, '');

    const filteredParameters = this.filterParameters(parameters, keys);

    const paramQueryString = this.createQueryString(filteredParameters ?? {});
    return paramQueryString && paramQueryString !== '' ? uri + '?' + paramQueryString : uri;
  }

  /** Transform the path string portion of a URI `atmosphere-websocket://localhost:8080/echo`, into a key-value object */
  static getParametersFromPathString(
    uriSyntax?: string,
    uriString?: string,
    options?: { requiredParameters?: string[] },
  ): ParsedParameters {
    /** If `:` is not present in the syntax, we can return an empty object since there's nothing to parse */
    if (!uriSyntax || !uriString || !uriSyntax.includes(':')) return {};

    /** Validate that the actual URI contains the correct schema, otherwise return empty object since we could be validating the wrong URI */
    if (!uriString.startsWith(uriSyntax.substring(0, uriSyntax.indexOf(':')))) return {};

    /** Prepare options */
    const requiredParameters = options?.requiredParameters ?? [];
    /** Holder for parsed parameters */
    const parameters: ParsedParameters = {};

    const { syntax: syntaxWithoutScheme, delimiters, delimiterRegex, keys } = this.analyzeSyntax(uriSyntax);
    const uriWithoutScheme = this.getUriWithoutScheme(uriString, uriSyntax);

    /** If the syntax does not contain any delimiters, we can return the URI string as is */
    if (delimiters === null && uriWithoutScheme !== '') {
      parameters[syntaxWithoutScheme] = uriWithoutScheme;
    } else if (delimiters !== null && delimiterRegex !== null) {
      /**
       * Split the URI string using the delimiters from the analyzed syntax.
       * keys: [ 'transport', 'host', 'port', 'messageName' ]
       * values: [ 'netty', 'localhost', '41414', 'foo' ]
       */
      const values = uriWithoutScheme.split(delimiterRegex);

      /**
       * There are special cases where some keys are not required, and the user didn't provide them.
       * This is the case for the `jms`, `amqp`, and `activemq` components, where the `destinationType` is not required,
       * so the user can provide the `destinationName` directly.
       * In this situation, if the values length matches the required parameters length,
       * we can remove the non-required key, so we can match the values with the keys.
       */
      if (requiredParameters.length < keys.length && requiredParameters.length === values.length) {
        const nonRequiredKeyIndex = keys.findIndex((key) => !requiredParameters.includes(key));
        if (nonRequiredKeyIndex !== -1) {
          keys.splice(nonRequiredKeyIndex, 1);
        }
      }

      keys.forEach((key, index) => {
        let parsedValue = getParsedValue(values[index]);
        const isLastItem = index === keys.length - 1;
        /** If the values length is greater than the keys length, we need to join the remaining values, f.i. ftp component */
        if (isLastItem && values.length > keys.length) {
          parsedValue = getParsedValue(values.slice(index).join(delimiters[index - 1]));
        }

        if (key !== '' && parsedValue !== '') {
          parameters[key] = parsedValue;
        }
      });
    }

    return parameters;
  }

  /** Analyze a URI syntax template once for both path serialization and parsing. */
  private static analyzeSyntax(uriSyntax: string): SyntaxAnalysis {
    const { schema, syntax } = this.getSyntaxWithoutSchema(uriSyntax);
    const delimiterMatches: string[] = [];
    let separatorMatch = this.URI_SEPARATORS_REGEX.exec(syntax);
    while (separatorMatch !== null) {
      delimiterMatches.push(separatorMatch[0]);
      separatorMatch = this.URI_SEPARATORS_REGEX.exec(syntax);
    }
    this.URI_SEPARATORS_REGEX.lastIndex = 0;
    const delimiters = delimiterMatches.length === 0 ? null : delimiterMatches;

    const delimiterRegex = delimiters === null ? null : new RegExp(delimiters.join('|'), 'g');
    const keys = delimiterRegex === null ? [syntax] : syntax.split(delimiterRegex);

    return { schema, syntax, delimiters, delimiterRegex, keys };
  }

  /** Transform the query string portion of a URI `param1=12&param2=hey`, into a key-value object */
  static getParametersFromQueryString(queryString?: string): ParsedParameters {
    return (
      queryString?.split('&').reduce((parameters, parameter) => {
        if (parameter) {
          const [key, stringValue] = parameter.split('=');
          parameters[key] = getParsedValue(stringValue);
        }

        return parameters;
      }, {} as ParsedParameters) ?? {}
    );
  }

  /**
   * Extract component name and kamelet name from a URI.
   *
   * Note: kameletName can only be present when componentName is 'kamelet'.
   * If there's no component, it's impossible to have a kamelet since kamelets
   * can only be used through the 'kamelet' component.
   *
   * @returns For regular components: { componentName: 'timer' }
   * @returns For kamelets: { componentName: 'kamelet', kameletName: 'beer-source' }
   * @returns For empty/invalid: { componentName: undefined }
   *
   * @example
   * // Regular component
   * getComponentAndKameletName('timer:tick')
   * // => { componentName: 'timer' }
   *
   * @example
   * // Kamelet component
   * getComponentAndKameletName('kamelet:beer-source')
   * // => { componentName: 'kamelet', kameletName: 'beer-source' }
   *
   * @example
   * // Kamelet with query parameters
   * getComponentAndKameletName('kamelet:beer-source?foo=bar')
   * // => { componentName: 'kamelet', kameletName: 'beer-source' }
   *
   * @example
   * // Kamelet with a routeId
   * getComponentAndKameletName('kamelet:beer-source/myRoute')
   * // => { componentName: 'kamelet', kameletName: 'beer-source' }
   */
  static getComponentAndKameletName(
    uri: string,
  ): { componentName: string | undefined } | { componentName: 'kamelet'; kameletName: string } {
    if (typeof uri !== 'string') {
      return { componentName: undefined };
    }

    const trimmedUri = uri.trim();
    if (trimmedUri === '') {
      return { componentName: undefined };
    }

    const separatorIndex = trimmedUri.indexOf(':');
    const componentName = separatorIndex === -1 ? trimmedUri : trimmedUri.slice(0, separatorIndex);

    if (!this.isValidComponentName(componentName)) {
      return { componentName: undefined };
    }

    if (componentName !== 'kamelet' || separatorIndex === -1) {
      return { componentName };
    }

    /** The kamelet component syntax is `kamelet:templateId/routeId`, the kamelet name is the `templateId` */
    const kameletSegment = trimmedUri.slice(separatorIndex + 1);
    const kameletName = kameletSegment.split('?')[0].split('/')[0];

    if (!this.isValidKameletName(kameletName)) {
      return { componentName: undefined };
    }

    return { componentName: 'kamelet', kameletName };
  }

  /**
   * Extract the component name and, when applicable, the Kamelet name from a processor definition.
   * Kamelets can be referenced in two ways, both are supported:
   *
   * @example
   * // Kamelet component with the templateId parameter (preferred)
   * getComponentAndKameletNameFromDefinition({ uri: 'kamelet', parameters: { templateId: 'beer-source' } })
   * // => { componentName: 'kamelet', kameletName: 'beer-source' }
   *
   * @example
   * // Kamelet name in the URI
   * getComponentAndKameletNameFromDefinition({ uri: 'kamelet:beer-source' })
   * // => { componentName: 'kamelet', kameletName: 'beer-source' }
   *
   * When both are present, `parameters.templateId` takes precedence over the URI path, matching
   * Camel's runtime behaviour: `Kamelet.extractTemplateId` checks the parameter map first.
   */
  static getComponentAndKameletNameFromDefinition(
    definition: unknown,
  ): ReturnType<typeof CamelUriHelper.getComponentAndKameletName> {
    const uri = this.getUriString(definition);
    if (!uri) {
      return { componentName: undefined };
    }

    const names = this.getComponentAndKameletName(uri);
    if (names.componentName !== 'kamelet') {
      return names;
    }

    const templateId = getValue(definition, 'parameters.templateId');
    if (typeof templateId === 'string' && this.isValidKameletName(templateId)) {
      return { componentName: 'kamelet', kameletName: templateId };
    }

    return names;
  }

  /**
   * Remove the scheme from the URI syntax:
   * 'avro:transport:host:port/messageName' => { schema: 'avro', syntax: 'transport:host:port/messageName' } */
  static getSyntaxWithoutSchema(uriSyntax: string): { schema: string; syntax: string } {
    const [schema, ...rest] = uriSyntax.split(':');
    const syntax = rest.join(':');
    return { schema, syntax };
  }

  /** Remove the scheme from the URI string: 'avro:netty:localhost:41414/foo' => 'netty:localhost:41414/foo' */
  private static getUriWithoutScheme(uriString: string, uriSyntax: string): string {
    const schemeEndIndex = uriSyntax.indexOf(':') + 1;
    let result = uriString.substring(schemeEndIndex);

    /**
     * If the URI uses :// (e.g. ftp://host:port/dir) but the syntax only uses :
     * (e.g. ftp:host:port/directoryName), strip the leading // so the URI
     * can be correctly matched against the syntax delimiters.
     */
    const syntaxAfterScheme = uriSyntax.substring(schemeEndIndex);
    if (result.startsWith('//') && !syntaxAfterScheme.startsWith('//')) {
      result = result.substring(2);
    }

    return result;
  }

  private static isValidComponentName(componentName: string): boolean {
    return this.COMPONENT_NAME_REGEX.test(componentName);
  }

  private static isValidKameletName(kameletName: string): boolean {
    return this.KAMELET_NAME_REGEX.test(kameletName) && !this.DANGEROUS_URI_SEGMENT_REGEX.test(kameletName);
  }
}
